import { FormulaManager } from '../managers/formula-manager';
import SheetManager from '../managers/sheet-manager';
import { WorkSheet } from '../core/WorkSheet';
import * as VTable from '@visactor/vtable';
import { getTablePlugins } from '../core/table-plugins';
import { DomEventManager } from '../event/dom-event-manager';
import { showSnackbar } from '../tools/ui/snackbar';
import type {
  IVTableSheetOptions,
  IVTableSheetUpdateOptions,
  ISheetDefine,
  IWorkSheetOptions,
  IColumnDefine,
  SheetData
} from '../ts-types';
import type { MultiSheetImportResult } from '@visactor/vtable-plugins';
import type { TableEventHandlersEventArgumentMap } from '@visactor/vtable/es/ts-types/events';
import SheetTabDragManager from '../managers/tab-drag-manager';
import { FormulaAutocomplete } from '../formula/formula-autocomplete';
import { createFormulaEditor } from '../formula/formula-editor';
import type { TYPES } from '@visactor/vtable';
import { MenuManager } from '../managers/menu-manager';
import { FormulaUIManager } from '../formula/formula-ui-manager';
import { SheetTabEventHandler } from './sheet-tab-event-handler';
import { TableEventRelay } from '../event/table-event-relay';
import type { VTableSheetEventType } from '../ts-types/spreadsheet-events';
import { SpreadSheetEventManager } from '../event/spreadsheet-event-manager';
import { VTableSheetEventBus } from '../event/vtable-sheet-event-bus';
import { pluginIsChanged } from '../sheet-helper';
import type { ITableThemeDefine } from '@visactor/vtable/es/themes';
import { tableThemeIsChanged } from '@visactor/vtable/es/themes';
import { WorkbookHistoryManager } from '../managers/workbook-history-manager';

export default class VTableSheet {
  /** DOM容器 */
  private container: HTMLElement;
  /** 配置选项 */
  private options: IVTableSheetOptions;
  /** sheet管理器 */
  private sheetManager: SheetManager;
  /** 公式管理器 */
  formulaManager: FormulaManager;
  /** 事件管理器 */
  private eventManager: DomEventManager;

  /** 菜单管理 */
  private menuManager: MenuManager;
  /** 当前活动sheet实例 */
  private activeWorkSheet: WorkSheet | null = null;
  /** 所有sheet实例 */
  workSheetInstances: Map<string, WorkSheet> = new Map();
  /** 公式自动补全 */
  private formulaAutocomplete: FormulaAutocomplete | null = null;
  /** Table 事件中转器 */
  private tableEventRelay: TableEventRelay;
  /** 电子表格事件管理器 */
  private spreadsheetEventManager: SpreadSheetEventManager;
  /** 统一事件总线 */
  private eventBus: VTableSheetEventBus;

  /** 公式UI管理器 */
  formulaUIManager: FormulaUIManager;

  /** UI组件 */
  private rootElement: HTMLElement;
  private formulaBarElement: HTMLElement | null = null;
  private sheetTabElement: HTMLElement | null = null;
  private mainMenuElement: HTMLElement | null = null;
  private undoRedoElement: HTMLElement | null = null;
  private contentElement: HTMLElement;

  // tab拖拽管理器
  private dragManager: SheetTabDragManager;
  /** sheet标签事件处理器 */
  private sheetTabEventHandler: SheetTabEventHandler;
  private workbookHistoryManager: WorkbookHistoryManager;

  /**
   * 构造函数
   * @param options 配置选项
   */
  constructor(container: HTMLElement, options: IVTableSheetOptions) {
    this.container = container;
    this.options = this.mergeDefaultOptions(options);

    // 创建统一事件总线
    this.eventBus = new VTableSheetEventBus();

    // 创建管理器（注意：tableEventRelay 必须在 eventManager 之前初始化）
    this.sheetManager = new SheetManager(this.eventBus);
    this.formulaManager = new FormulaManager(this);
    this.tableEventRelay = new TableEventRelay(this); // ⚠️ 必须在 EventManager 之前初始化
    this.eventManager = new DomEventManager(this); // EventManager 构造函数会调用 this.onTableEvent()
    this.dragManager = new SheetTabDragManager(this);
    this.formulaUIManager = new FormulaUIManager(this);
    this.sheetTabEventHandler = new SheetTabEventHandler(this);
    this.spreadsheetEventManager = new SpreadSheetEventManager(this);
    this.workbookHistoryManager = new WorkbookHistoryManager(this);
    this.menuManager = new MenuManager(this);

    // 初始化UI
    this.initUI();

    // 初始化sheets
    this.workbookHistoryManager.suspend();
    this.initSheets();
    this.workbookHistoryManager.resume();

    this.resize();
  }

  /**
   * 合并默认配置
   */
  private mergeDefaultOptions(options: IVTableSheetOptions): IVTableSheetOptions {
    return {
      showFormulaBar: true,
      showSheetTab: true,
      defaultRowHeight: 25,
      defaultColWidth: 100,
      ...options
    };
  }

  /**
   * 初始化公式自动补全
   */
  private initFormulaAutocomplete(): void {
    if (!this.formulaBarElement) {
      return;
    }

    const formulaInput = this.formulaUIManager.formulaInput;
    if (formulaInput) {
      this.formulaAutocomplete = new FormulaAutocomplete(this.rootElement, this);
      this.formulaAutocomplete.attachTo(formulaInput);
    }
  }

  /**
   * 初始化UI
   */
  private initUI(): void {
    // 创建根元素
    this.rootElement = document.createElement('div');
    this.rootElement.className = 'vtable-sheet-container';
    // this.rootElement.style.width = `${this.options.width}px`;
    // this.rootElement.style.height = `${this.options.height}px`;
    this.container.appendChild(this.rootElement);
    //创建顶部菜单和公式的容器
    const topContainer = document.createElement('div');
    topContainer.className = 'vtable-sheet-top-container';
    this.rootElement.appendChild(topContainer);

    this.renderTopLeftControls(topContainer);
    // 创建公式栏
    if (this.options.showFormulaBar) {
      this.formulaBarElement = this.formulaUIManager.createFormulaBar();
      topContainer.appendChild(this.formulaBarElement);

      this.initFormulaAutocomplete();
    }

    // 创建内容区域
    this.contentElement = document.createElement('div');
    this.contentElement.className = 'vtable-sheet-content';
    this.rootElement.appendChild(this.contentElement);

    // 创建sheet切换栏
    if (this.options.showSheetTab) {
      this.sheetTabElement = this.createSheetTab();
      this.rootElement.appendChild(this.sheetTabElement);
    }
  }

  private renderTopLeftControls(topContainer: HTMLElement): void {
    if (this.mainMenuElement?.parentElement) {
      this.mainMenuElement.parentElement.removeChild(this.mainMenuElement);
      this.mainMenuElement = null;
    }
    if (this.undoRedoElement?.parentElement) {
      this.undoRedoElement.parentElement.removeChild(this.undoRedoElement);
      this.undoRedoElement = null;
    }
    this.menuManager.release();

    const showMainMenu = !!this.options.mainMenu?.show;
    const showUndoRedo = this.options.undoRedo?.show ?? true;
    if (showMainMenu) {
      this.mainMenuElement = this.menuManager.createMainMenu();
      if (topContainer.firstChild) {
        topContainer.insertBefore(this.mainMenuElement, topContainer.firstChild);
      } else {
        topContainer.appendChild(this.mainMenuElement);
      }
      return;
    }
    if (showUndoRedo) {
      this.undoRedoElement = this.menuManager.createUndoRedoOnly();
      if (topContainer.firstChild) {
        topContainer.insertBefore(this.undoRedoElement, topContainer.firstChild);
      } else {
        topContainer.appendChild(this.undoRedoElement);
      }
    }
  }

  /**
   * 创建sheet切换栏
   */
  private createSheetTab(): HTMLElement {
    // SVG图标常量
    const addIcon =
      '<svg viewBox="0 0 24 24" width="16" height="16">' +
      '<path fill="currentColor" d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6z"/>' +
      '</svg>';
    const leftIcon =
      '<svg viewBox="0 0 24 24" width="16" height="16">' +
      '<path fill="currentColor" d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>' +
      '</svg>';
    const rightIcon =
      '<svg viewBox="0 0 24 24" width="16" height="16">' +
      '<path fill="currentColor" d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>' +
      '</svg>';
    const menuIcon =
      '<svg viewBox="0 0 24 24" width="16" height="16">' +
      '<path fill="currentColor" d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 ' +
      '.9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>' +
      '</svg>';

    const sheetTab = document.createElement('div');
    sheetTab.className = 'vtable-sheet-tab-bar';

    // 创建左侧渐变效果
    const fadeLeft = document.createElement('div');
    fadeLeft.className = 'vtable-sheet-fade-left';
    fadeLeft.style.display = 'none';
    sheetTab.appendChild(fadeLeft);

    // 创建中间的tabs容器
    const tabsContainer = document.createElement('div');
    tabsContainer.className = 'vtable-sheet-tabs-container';
    tabsContainer.addEventListener('scroll', () =>
      this.sheetTabEventHandler.updateFadeEffects(tabsContainer, fadeLeft, fadeRight)
    );
    sheetTab.appendChild(tabsContainer);

    // 创建插入指示器
    const insertIndicator = document.createElement('div');
    insertIndicator.className = 'vtable-sheet-insert-indicator';
    insertIndicator.style.display = 'none';
    tabsContainer.appendChild(insertIndicator);

    // 创建右侧渐变效果
    const fadeRight = document.createElement('div');
    fadeRight.className = 'vtable-sheet-fade-right';
    sheetTab.appendChild(fadeRight);

    // 添加新增sheet按钮
    const addButton = document.createElement('button');
    addButton.className = 'vtable-sheet-add-button';
    addButton.innerHTML = addIcon;
    addButton.title = '添加工作表';
    addButton.addEventListener('click', () => this._addNewSheet());
    sheetTab.appendChild(addButton);

    // 创建导航按钮容器
    const navButtons = document.createElement('div');
    navButtons.className = 'vtable-sheet-nav-buttons';

    // 创建左侧滚动按钮
    const leftScrollBtn = document.createElement('button');
    leftScrollBtn.className = 'vtable-sheet-scroll-button';
    leftScrollBtn.innerHTML = leftIcon;
    leftScrollBtn.title = '向左滚动';
    leftScrollBtn.addEventListener('click', () => this.sheetTabEventHandler.scrollSheetTabs('left', tabsContainer));
    navButtons.appendChild(leftScrollBtn);

    // 创建右侧滚动按钮
    const rightScrollBtn = document.createElement('button');
    rightScrollBtn.className = 'vtable-sheet-scroll-button';
    rightScrollBtn.innerHTML = rightIcon;
    rightScrollBtn.title = '向右滚动';
    rightScrollBtn.addEventListener('click', () => this.sheetTabEventHandler.scrollSheetTabs('right', tabsContainer));
    navButtons.appendChild(rightScrollBtn);

    // 创建sheet菜单按钮
    const menuButton = document.createElement('button');
    menuButton.className = 'vtable-sheet-menu-button';
    menuButton.innerHTML = menuIcon;
    menuButton.title = '工作表选项';
    menuButton.addEventListener('click', e => this.sheetTabEventHandler.toggleSheetMenu(e));
    navButtons.appendChild(menuButton);

    // 创建菜单容器
    const menuContainer = document.createElement('ul');
    menuContainer.className = 'vtable-sheet-menu-list';
    sheetTab.appendChild(menuContainer);

    sheetTab.appendChild(navButtons);
    // 初始化渐变效果
    setTimeout(() => {
      this.sheetTabEventHandler.updateFadeEffects(tabsContainer, fadeLeft, fadeRight);
    }, 100);

    return sheetTab;
  }

  /**
   * 激活sheet标签并滚动到可见区域
   */
  private _activeSheetTab(): void {
    if (this.options.showSheetTab === false) {
      return;
    }
    this.sheetTabEventHandler.activeSheetTab();
  }
  /**
   * 更新sheet切换标签
   * @param tabsContainer 标签容器
   */
  updateSheetTabs(
    tabsContainer: HTMLElement = this.sheetTabElement?.querySelector('.vtable-sheet-tabs-container')
  ): void {
    if (!tabsContainer) {
      return;
    }
    // 清除现有标签 - 直接清空容器内容（这会移除所有事件监听器）
    tabsContainer.innerHTML = '';

    // 添加sheet标签
    const sheets = this.sheetManager.getAllSheets();

    sheets.forEach((sheet, index) => {
      tabsContainer.appendChild(this.createSheetTabItem(sheet, index));
    });
    // 激活sheet标签并滚动到可见区域
    this._activeSheetTab();
  }
  /**
   * 创建tab栏标签项
   */
  private createSheetTabItem(sheet: ISheetDefine, _index: number): HTMLElement {
    const tab = document.createElement('div');
    tab.className = 'vtable-sheet-tab';
    tab.dataset.key = sheet.sheetKey;
    tab.textContent = sheet.sheetTitle;
    tab.title = sheet.sheetTitle;
    tab.addEventListener('click', () => this.activateSheet(sheet.sheetKey));
    tab.addEventListener('dblclick', () =>
      this.sheetTabEventHandler.handleSheetTabDblClick(sheet.sheetKey, sheet.sheetTitle)
    );
    // 拖拽事件
    tab.addEventListener('mousedown', e => this.dragManager.handleTabMouseDown(e, sheet.sheetKey));

    return tab;
  }

  /**
   * 更新sheet列表
   */
  updateSheetMenu(): void {
    if (this.options.showSheetTab === false) {
      return;
    }
    this.sheetTabEventHandler.updateSheetMenu();
  }

  /**
   * 初始化sheets
   */
  private initSheets(): void {
    if (this.options.sheets && this.options.sheets.length > 0) {
      // 添加所有sheet
      this.options.sheets.forEach((sheetDefine: ISheetDefine) => {
        this.sheetManager.addSheet(sheetDefine);
      });

      // 找到active的sheet
      let activeSheetKey = '';
      const activeSheet = this.options.sheets.find((sheet: ISheetDefine) => sheet.active);
      if (activeSheet) {
        activeSheetKey = activeSheet.sheetKey;
      } else {
        activeSheetKey = this.options.sheets[0].sheetKey;
      }

      // 激活sheet
      this.activateSheet(activeSheetKey);
    } else {
      // 如果没有提供sheets，创建一个默认的
      this._addNewSheet();
    }
  }

  /**
   * 激活指定sheet
   * @param sheetKey sheet的key
   */
  activateSheet(sheetKey: string): void {
    // 获取之前激活的sheet信息
    const previousActiveSheet = this.sheetManager.getActiveSheet();
    const previousSheetKey = previousActiveSheet?.sheetKey;
    const previousSheetTitle = previousActiveSheet?.sheetTitle;

    // 设置活动sheet
    this.sheetManager.setActiveSheet(sheetKey);

    // 获取sheet定义
    const sheetDefine = this.sheetManager.getSheet(sheetKey);
    if (!sheetDefine) {
      return;
    }

    // 隐藏所有sheet实例并解除事件绑定
    this.workSheetInstances.forEach(instance => {
      instance.getElement().style.display = 'none';
      // 解除事件绑定以防止重复触发
      if (instance.tableInstance) {
        this.tableEventRelay.unbindSheetEvents(instance.sheetKey, instance.tableInstance);
      }
    });

    // 如果已经存在实例，则显示并激活对应tab和menu
    if (this.workSheetInstances.has(sheetKey)) {
      const instance = this.workSheetInstances.get(sheetKey);
      if (!instance) {
        throw new Error(`Worksheet instance for key "${sheetKey}" not found`);
      }
      instance.getElement().style.display = 'block';
      this.activeWorkSheet = instance;

      // 重新绑定事件（因为我们在隐藏时解除了绑定）
      if (instance.tableInstance) {
        this.tableEventRelay.bindSheetEvents(instance.sheetKey, instance.tableInstance);
      }

      // 更新公式管理器中的活动工作表（在实例激活后）
      this.formulaManager.setActiveSheet(sheetKey);

      // sheet标签和菜单项激活样式
      this._activeSheetTab();
      this.sheetTabEventHandler.activeSheetMenuItem();

      // 恢复筛选状态
      this.restoreFilterState(instance, sheetDefine);
    } else {
      // 创建新的sheet实例
      const instance = this.createWorkSheetInstance(sheetDefine);
      this.workSheetInstances.set(sheetKey, instance);
      this.activeWorkSheet = instance;

      // 更新公式管理器中的活动工作表（在实例创建后）
      this.formulaManager.setActiveSheet(sheetKey);

      // 刷新sheet标签和菜单
      this.updateSheetTabs();
      this.updateSheetMenu();

      // 恢复筛选状态
      this.restoreFilterState(instance, sheetDefine);
    }

    this.updateFormulaBar();

    // 触发工作表激活事件（电子表格级别）
    this.spreadsheetEventManager.emitSheetActivated(
      sheetKey,
      sheetDefine.sheetTitle,
      previousSheetKey,
      previousSheetTitle
    );

    // 触发之前工作表的停用事件
    if (previousSheetKey && previousSheetKey !== sheetKey) {
      this.spreadsheetEventManager.emitSheetDeactivated(previousSheetKey, previousSheetTitle);
    }
  }

  addSheet(sheet: ISheetDefine): void {
    this.workbookHistoryManager.addSheet({ sheet, activate: false });
  }

  /**
   * 删除sheet
   * @param sheetKey 工作表key
   */
  removeSheet(sheetKey: string): void {
    if (this.sheetManager.getSheetCount() <= 1) {
      showSnackbar('至少保留一个工作表', 1300);
      return;
    }
    this.workbookHistoryManager.removeSheet(sheetKey);
  }
  getSheetCount(): number {
    return this.sheetManager.getSheetCount();
  }
  getSheet(sheetKey: string): ISheetDefine | null {
    return this.sheetManager.getSheet(sheetKey);
  }
  getAllSheets(): ISheetDefine[] {
    return this.sheetManager.getAllSheets();
  }

  /**
   * 创建sheet实例
   * @param sheetDefine sheet的定义
   */
  createWorkSheetInstance(sheetDefine: ISheetDefine): WorkSheet {
    // 计算内容区域大小
    const contentWidth = this.contentElement.clientWidth;
    const contentHeight = this.contentElement.clientHeight;
    sheetDefine.dragOrder = sheetDefine.dragOrder ?? this.options.dragOrder;
    const formulaEditor = createFormulaEditor();
    // 创建sheet实例
    const sheet = new WorkSheet(this, {
      ...sheetDefine,
      container: this.contentElement,
      width: contentWidth,
      height: contentHeight,
      defaultRowHeight: this.options.defaultRowHeight,
      defaultColWidth: this.options.defaultColWidth,
      dragOrder: sheetDefine.dragOrder,
      plugins: getTablePlugins(sheetDefine, this.options, this),
      headerEditor: formulaEditor,
      editor: formulaEditor,
      select: {
        makeSelectCellVisible: false
      },
      // style: {
      //   borderColor: ['#E1E4E8', '#E1E4E8', '#E1E4E8', '#E1E4E8'],
      //   borderLineWidth: [1, 1, 1, 1],
      //   borderLineDash: [null, null, null, null],
      //   padding: [8, 8, 8, 8]
      // },
      editCellTrigger: ['api', 'keydown', 'doubleclick'],
      customMergeCell: sheetDefine.cellMerge,
      theme: sheetDefine.theme?.tableTheme || this.options.theme?.tableTheme
    });
    formulaEditor.setSheet(this);

    // 事件系统现在通过 TableEventRelay 自动处理，不再需要手动绑定

    // 在公式管理器中添加这个sheet
    try {
      const normalizedData = sheetDefine.data
        ? this.formulaManager.normalizeSheetData(sheetDefine.data, sheet.tableInstance)
        : [];
      this.formulaManager.addSheet(sheetDefine.sheetKey, normalizedData, sheetDefine.sheetTitle);
      // 加载保存的公式数据（如果有）
      if (sheetDefine.formulas && Object.keys(sheetDefine.formulas).length > 0) {
        this.loadFormulas(sheetDefine.sheetKey, sheetDefine.formulas);
      }
    } catch (error) {
      console.warn(`Sheet ${sheetDefine.sheetKey} may already exist in formula manager:`, error);
      // 如果添加失败（可能已存在），继续执行
    }

    return sheet;
  }
  /**
   * 加载指定工作表的公式数据
   * @param sheetKey 工作表键
   * @param formulas 公式数据 (A1表示法的单元格引用 -> 公式内容)
   */
  private loadFormulas(sheetKey: string, formulas: Record<string, string>): void {
    if (!formulas || Object.keys(formulas).length === 0) {
      return;
    }

    try {
      // 优化公式计算顺序
      const sortedFormulas = this.formulaManager.sortFormulasByDependency(sheetKey, formulas);
      // 按照优化后的顺序设置公式
      for (const [cellRef, formula] of sortedFormulas) {
        // 解析单元格引用 (如 A1, B2) 到行列索引
        const { row, col } = this.parseCellKey(cellRef);
        // 设置单元格公式
        this.formulaManager.setCellContent({ sheet: sheetKey, row, col }, formula);
      }
    } catch (error) {
      console.error(`Failed to load formulas for sheet ${sheetKey}:`, error);
    }
  }

  /**
   * 将单元格引用（A1表示法）解析为行列索引
   * @param cellKey 单元格引用 (如 A1, B2)
   * @returns 行列索引 (0-based)
   */
  private parseCellKey(cellKey: string): { row: number; col: number } {
    // 匹配列引用（字母部分）和行引用（数字部分）
    const match = cellKey.match(/^([A-Za-z]+)(\d+)$/);
    if (!match) {
      throw new Error(`Invalid cell reference: ${cellKey}`);
    }
    const [, colStr, rowStr] = match;
    // 解析行索引 (1-based -> 0-based)
    const row = parseInt(rowStr, 10) - 1;
    // 解析列索引 (A -> 0, B -> 1, ..., Z -> 25, AA -> 26, etc.)
    let col = 0;
    for (let i = 0; i < colStr.length; i++) {
      const charCode = colStr.toUpperCase().charCodeAt(i) - 65; // 65 is ASCII for 'A'
      col = col * 26 + charCode;
    }
    return { row, col };
  }

  /**
   * 恢复筛选状态
   */
  private restoreFilterState(sheet: WorkSheet, sheetDefine: ISheetDefine): void {
    // 如果没有保存的筛选状态，直接返回
    if (!sheetDefine.filterState) {
      return;
    }

    // console.log(`恢复 Sheet ${sheetDefine.sheetKey} 的筛选状态:`, sheetDefine.filterState);

    // 等待表格初始化完成
    setTimeout(() => {
      if (sheet.tableInstance && sheet.tableInstance.pluginManager) {
        const filterPlugin = sheet.tableInstance.pluginManager.getPluginByName('Filter') as any;
        if (filterPlugin) {
          filterPlugin.setFilterState(sheetDefine.filterState);
        } else {
          console.warn(`Sheet ${sheetDefine.sheetKey} 未找到筛选插件或插件不支持 setFilterState 方法`);
        }
      } else {
        console.warn(`Sheet ${sheetDefine.sheetKey} 表格实例或插件管理器未初始化`);
      }
    }, 0);
  }

  /**
   * 添加新sheet
   */
  private _addNewSheet(): void {
    // 生成新sheet的key和title
    const sheetCount = this.sheetManager.getSheetCount();
    const baseKey = `sheet${sheetCount + 1}`;
    const baseTitle = `Sheet ${sheetCount + 1}`;
    let key = baseKey;
    let title = baseTitle;
    let index = sheetCount + 1;
    // 检查key和title是否被占用，递增直到唯一
    const existingKeys = new Set(this.sheetManager.getAllSheets().map(s => s.sheetKey));
    const existingTitles = new Set(this.sheetManager.getAllSheets().map(s => s.sheetTitle));
    while (existingKeys.has(key) || existingTitles.has(title)) {
      index += 1;
      key = `sheet${index}`;
      title = `Sheet ${index}`;
    }

    // 创建新sheet配置
    const newSheet: ISheetDefine = {
      sheetKey: key,
      sheetTitle: title,
      columnCount: 20,
      rowCount: 100,
      data: []
    };

    this.workbookHistoryManager.addSheet({ sheet: newSheet, activate: true });
  }

  /**
   * 更新公式栏
   */
  private updateFormulaBar(): void {
    this.formulaUIManager.updateFormulaBar();
  }

  /**
   * 获取公式管理器
   */
  getFormulaManager(): FormulaManager {
    return this.formulaManager;
  }
  /**
   * 获取电子表格事件管理器
   */
  getSpreadSheetEventManager(): SpreadSheetEventManager {
    return this.spreadsheetEventManager;
  }

  /**
   * 获取统一事件总线
   */
  getEventBus(): VTableSheetEventBus {
    return this.eventBus;
  }

  /**
   * 获取Sheet管理器
   */
  getSheetManager(): SheetManager {
    return this.sheetManager;
  }
  getWorkbookHistoryManager(): WorkbookHistoryManager {
    return this.workbookHistoryManager;
  }

  /**
   * 获取活动Sheet实例
   */
  getActiveSheet(): WorkSheet | null {
    return this.activeWorkSheet;
  }
  getWorkSheetInstance(sheetKey: string): WorkSheet | undefined {
    return this.workSheetInstances.get(sheetKey);
  }
  setWorkSheetInstance(sheetKey: string, instance: WorkSheet): void {
    this.workSheetInstances.set(sheetKey, instance);
  }
  deleteWorkSheetInstance(sheetKey: string): void {
    this.workSheetInstances.delete(sheetKey);
  }
  renameSheet(sheetKey: string, newTitle: string): void {
    this.workbookHistoryManager.renameSheet(sheetKey, newTitle);
  }
  reorderSheet(sourceKey: string, targetKey: string, position: 'left' | 'right'): void {
    this.workbookHistoryManager.reorderSheet(sourceKey, targetKey, position);
  }
  undo(): void {
    this.workbookHistoryManager.undo();
  }
  redo(): void {
    this.workbookHistoryManager.redo();
  }
  startHistoryTransaction(): void {
    this.workbookHistoryManager.startTransaction();
  }
  endHistoryTransaction(): void {
    this.workbookHistoryManager.endTransaction();
  }

  /**
   * 监听 Table 事件（统一监听所有 sheet）
   *
   * 提供通用的事件转发机制
   * 当任何 sheet 触发事件时，回调函数会自动接收到增强的事件对象（附带 sheetKey）
   *
   * @example
   * ```typescript
   * // 监听所有 sheet 的单元格点击
   * sheet.onTableEvent('click_cell', (event) => {
   *   // event.sheetKey 告诉你是哪个 sheet
   *   // event 的其他属性是原始 VTable 事件
   *   console.log(`Sheet ${event.sheetKey} 的单元格 [${event.row}, ${event.col}] 被点击`);
   * });
   *
   * // 监听所有 sheet 的单元格值改变
   * sheet.onTableEvent('change_cell_value', (event) => {
   *   console.log(`Sheet ${event.sheetKey} 的值改变`);
   *   autoSave(event);
   * });
   *
   * // 可以监听任何 VTable 支持的事件
   * sheet.onTableEvent('scroll', (event) => {
   *   console.log(`Sheet ${event.sheetKey} 滚动了`);
   * });
   * ```
   *
   * @param type VTable 事件类型
   * @param callback 事件回调函数，参数是增强后的事件对象（包含 sheetKey）
   */
  onTableEvent<K extends keyof TableEventHandlersEventArgumentMap>(
    type: K,
    callback: (event: TableEventHandlersEventArgumentMap[K] & { sheetKey: string }) => void
  ): void {
    this.tableEventRelay.onTableEvent(type, callback);
  }

  /**
   * 移除 Table 事件监听器
   *
   * @param type VTable 事件类型
   * @param callback 事件回调函数（可选）
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  offTableEvent(type: string, callback?: (...args: any[]) => void): void {
    this.tableEventRelay.offTableEvent(type, callback);
  }

  /**
   * 注册 WorkSheet 事件监听器（在 VTableSheet 层）
   */
  onSheetEvent(type: string, callback: (event: any) => void): void {
    // 所有事件都通过 SpreadSheetEventManager 处理
    // 事件系统会自动处理工作表级别的事件分发
    this.spreadsheetEventManager.on(type as any, callback);
  }

  /**
   * 移除 WorkSheet 事件监听器
   *
   * @param type 事件类型
   * @param callback 回调函数（可选）
   */
  offSheetEvent(type: string, callback?: (event: any) => void): void {
    // 所有事件都通过 SpreadSheetEventManager 处理
    // 事件系统会自动处理工作表级别的事件移除
    this.spreadsheetEventManager.off(type as any, callback);
  }

  /**
   * 注册事件监听器（统一接口）
   *
   * 推荐使用此方法替代 onSheetEvent，提供更简洁的 API
   *
   * @param type 事件类型
   * @param callback 事件回调函数
   */
  on(type: VTableSheetEventType, callback: (event: any) => void): void {
    this.onSheetEvent(type, callback);
  }

  /**
   * 移除事件监听器（统一接口）
   *
   * 推荐使用此方法替代 offSheetEvent，提供更简洁的 API
   *
   * @param type 事件类型
   * @param callback 事件回调函数（可选）
   */
  off(type: VTableSheetEventType, callback?: (event: any) => void): void {
    this.offSheetEvent(type, callback);
  }

  /**
   * 根据名称获取Sheet实例
   */
  getSheetByName(sheetName: string): WorkSheet | null {
    // 遍历所有sheet实例，找到匹配的sheet
    for (const [sheetKey, workSheet] of this.workSheetInstances) {
      const sheetDefine = this.sheetManager.getSheet(sheetKey);
      if (sheetDefine && sheetDefine.sheetTitle === sheetName) {
        return workSheet;
      }
    }
    return null;
  }

  /**
   * 根据key获取Sheet实例
   */
  getWorkSheetByKey(sheetKey: string): WorkSheet | null {
    return this.workSheetInstances.get(sheetKey) || null;
  }

  /**
   * 保存所有数据为配置
   */
  saveToConfig(): IVTableSheetOptions {
    // 收集所有sheet的数据
    const sheets: ISheetDefine[] = [];

    this.sheetManager.getAllSheets().forEach(sheetDefine => {
      const instance = this.workSheetInstances.get(sheetDefine.sheetKey);
      if (instance) {
        const data = instance.getCopiedData();
        //#region 组织columns
        //column中去除field字段 (field字段会在columns.map中被使用)
        const columns = instance.getColumns().map(column => {
          // 解构时省略field属性
          const { ...rest } = column;
          // 删除field属性
          delete rest.field;
          return rest;
        });
        // 找到最后一个有title的列的索引
        const lastTitleIndex = columns.reduce((lastIndex, column, index) => (column.title ? index : lastIndex), -1);

        // 从最后一个有title的列之后删除所有列
        if (lastTitleIndex === -1) {
          columns.length = 0; // 清空数组
        } else {
          columns.splice(lastTitleIndex + 1);
        }
        //#endregion

        //#region 组织data
        // 找到最后一个有非空值的行
        const lastDataIndex = data.reduce((lastIndex, rowData, index) => (rowData ? index : lastIndex), -1);
        // 保留到最后一个有值的行，删除之后的空行
        if (lastDataIndex === -1) {
          data.length = 0; // 清空数组
        } else {
          data.splice(lastDataIndex + 1);
        }
        //#endregion
        // 获取筛选状态
        let filterState = null;
        const filterPlugin = instance.tableInstance.pluginManager.getPluginByName('Filter') as any;
        if (filterPlugin) {
          filterState = filterPlugin.getFilterState();
        }

        // 获取排序状态
        let sortState = instance.tableInstance.internalProps.sortState;
        let currentSortState;
        if (sortState) {
          sortState = Array.isArray(sortState) ? sortState : [sortState];
          currentSortState = sortState.map(item => ({
            field: item.field,
            order: item.order,
            ...(item.orderFn !== null && item.orderFn !== undefined && { orderFn: item.orderFn })
          }));
        }

        // 使用FormulaManager的导出方法获取所有公式
        const formulas = this.formulaManager.exportFormulas(sheetDefine.sheetKey);

        //#region 从tableInstance.internalProps._widthResizedColMap对应到columns的key 组织columnWidthConfig
        const columnWidthConfig = Array.from(instance.tableInstance.internalProps._widthResizedColMap).map(key => {
          return {
            key: key,
            width: instance.tableInstance.getColWidth(key as number)
          };
        });
        //#endregion
        //#region 从tableInstance.internalProps._heightResizedRowMap对应到columns的key 组织rowHeightConfig
        const rowHeightConfig = Array.from(instance.tableInstance.internalProps._heightResizedRowMap).map(key => {
          return {
            key: key,
            height: instance.tableInstance.getRowHeight(key)
          };
        });
        //#endregion

        sheets.push({
          ...sheetDefine,
          data,
          columns: columns as IColumnDefine[],
          cellMerge: instance.tableInstance.options.customMergeCell as TYPES.CustomMergeCellArray,
          showHeader: instance.tableInstance.options.showHeader,
          frozenRowCount: instance.tableInstance.frozenRowCount,
          frozenColCount: instance.tableInstance.frozenColCount,
          active: sheetDefine.sheetKey === this.sheetManager.getActiveSheet().sheetKey,
          filterState: filterState,
          sortState: currentSortState,
          formulas: Object.keys(formulas).length > 0 ? formulas : undefined,
          columnWidthConfig,
          rowHeightConfig
        });
      } else {
        sheets.push(sheetDefine);
      }
    });

    return {
      ...this.options,
      sheets
    };
  }
  updateMainMenu(mainMenu: IVTableSheetOptions['mainMenu']): void {
    this.options.mainMenu = mainMenu;
    const topContainer = this.rootElement.querySelector('.vtable-sheet-top-container') as HTMLElement | null;
    if (!topContainer) {
      return;
    }
    this.renderTopLeftControls(topContainer);
  }
  /**
   * 更新电子表格配置
   *
   * - 当 options 中包含 sheets 时，视为“全量更新”，会对比新旧 sheets 列表并执行新增/删除/更新逻辑；
   * - 当 options 中不包含 sheets 时，视为“增量更新”，仅对常用全局配置进行局部更新，并下发到受影响的 WorkSheet。
   */
  updateOption(options: IVTableSheetUpdateOptions): void {
    if (!options) {
      return;
    }
    const hasMainMenu = typeof options.mainMenu !== 'undefined';
    const hasUndoRedo = typeof (options as any).undoRedo !== 'undefined';
    const pluginModulesChanged = pluginIsChanged(this.options.VTablePluginModules, options.VTablePluginModules);
    const tableThemeChanged = tableThemeIsChanged(this.options.theme?.tableTheme, options.theme?.tableTheme);
    //对options中非sheets的配置项逐项进行分析
    const { sheets, ...rest } = options;
    // if (Object.keys(rest).length > 0) {
    //   this.updateGlobalOptions(rest);
    // }

    // 先更新顶层 options（保持最新配置，用于后续新增 sheet 等场景）
    this.options = {
      ...this.options,
      ...(options as IVTableSheetOptions)
    };

    if (hasMainMenu || hasUndoRedo) {
      const topContainer = this.rootElement.querySelector('.vtable-sheet-top-container') as HTMLElement | null;
      if (topContainer) {
        this.renderTopLeftControls(topContainer);
      }
    }

    // 如果包含 sheets，按全量更新处理
    if (Array.isArray(options.sheets) || pluginModulesChanged || tableThemeChanged) {
      this.updateSheets(options);
    } else {
      // 仅做增量更新
      // this.updateGlobalOptions(options);
    }
  }

  /**
   * 全量更新 sheets：对比新旧列表，执行新增 / 删除 / 更新
   *
   * - 新增：创建 WorkSheet 实例并绑定事件、注册公式；
   * - 删除：释放 WorkSheet 实例和底层表格、移除公式；
   * - 更新：复用已存在的 WorkSheet 实例，调用 updateSheetOption 做增量更新。
   *
   * 完成结构变更后，通过 FormulaManager 重建公式依赖，确保跨表引用一致。
   */
  private updateSheets(options: IVTableSheetUpdateOptions): void {
    const nextSheets = options.sheets || [];
    const prevSheets = this.sheetManager.getAllSheets();

    const prevMap = new Map<string, ISheetDefine>();
    prevSheets.forEach(sheet => {
      prevMap.set(sheet.sheetKey, sheet);
    });

    const added: ISheetDefine[] = [];
    const updated: { prev: ISheetDefine; next: ISheetDefine }[] = [];

    nextSheets.forEach(next => {
      const prev = prevMap.get(next.sheetKey);
      if (!prev) {
        added.push(next);
      } else {
        updated.push({ prev, next });
        prevMap.delete(next.sheetKey);
      }
    });

    const removedKeys = Array.from(prevMap.keys());

    // 先删除移除的 sheet
    removedKeys.forEach(sheetKey => {
      const instance = this.workSheetInstances.get(sheetKey);
      if (instance) {
        instance.release();
        this.workSheetInstances.delete(sheetKey);
      }
      try {
        this.sheetManager.removeSheet(sheetKey);
        this.formulaManager.removeSheet(sheetKey);
      } catch (error) {
        console.warn(`Failed to remove sheet ${sheetKey}:`, error);
      }
    });

    // 新增 sheet
    added.forEach(sheetDefine => {
      this.sheetManager.addSheet(sheetDefine);
      const instance = this.createWorkSheetInstance(sheetDefine);
      this.workSheetInstances.set(sheetDefine.sheetKey, instance);
    });

    // 更新已存在的 sheet：优先使用 WorkSheet.updateSheetOption 做增量更新
    updated.forEach(({ prev, next: next_sheetDefine }) => {
      const instance = this.workSheetInstances.get(next_sheetDefine.sheetKey);

      // 将最新配置合并回 SheetManager 持有的定义对象，保证后续 getAllSheets 返回的是最新配置
      Object.assign(prev, next_sheetDefine);

      if (!instance) {
        const newInstance = this.createWorkSheetInstance(next_sheetDefine);
        this.workSheetInstances.set(next_sheetDefine.sheetKey, newInstance);
        return;
      }
      const formulaEditor = createFormulaEditor();
      const sheetOption: IWorkSheetOptions = {
        sheetTitle: next_sheetDefine.sheetTitle,
        sheetKey: next_sheetDefine.sheetKey,
        showHeader: next_sheetDefine.showHeader,
        frozenRowCount: next_sheetDefine.frozenRowCount,
        frozenColCount: next_sheetDefine.frozenColCount,
        filter: next_sheetDefine.filter,
        filterState: next_sheetDefine.filterState,
        sortState: next_sheetDefine.sortState,
        columnWidthConfig: next_sheetDefine.columnWidthConfig,
        rowHeightConfig: next_sheetDefine.rowHeightConfig,
        defaultRowHeight: this.options.defaultRowHeight,
        defaultColWidth: this.options.defaultColWidth,
        dragOrder: next_sheetDefine.dragOrder,
        plugins: getTablePlugins(next_sheetDefine, this.options, this),
        headerEditor: formulaEditor,
        editor: formulaEditor,
        select: {
          makeSelectCellVisible: false
        },
        editCellTrigger: ['api', 'keydown', 'doubleclick'],
        customMergeCell: next_sheetDefine.cellMerge,
        theme: next_sheetDefine.theme?.tableTheme || this.options.theme?.tableTheme,
        data: next_sheetDefine.data,
        columns: next_sheetDefine.columns
        // // 传入 data/columns 以便 updateOption 能正确更新表格数据；显式传 columns: undefined 时也要透传，以便恢复为无表头
        // ...('data' in next_sheetDefine && { data: next_sheetDefine.data }),
        // ...('columns' in next_sheetDefine && { columns: next_sheetDefine.columns })
      };
      formulaEditor.setSheet(this);
      instance.updateSheetOption(sheetOption);
    });

    // 结构变更完成后，重建所有公式与依赖
    try {
      this.formulaManager.rebuildFormulas(nextSheets);
      // 重建后同步每个 sheet 的数据并重新加载公式
      nextSheets.forEach(sheetDefine => {
        const worksheetInstance = this.workSheetInstances.get(sheetDefine.sheetKey);
        if (!worksheetInstance || !worksheetInstance.tableInstance) {
          return;
        }
        const dataToUse = sheetDefine.data || worksheetInstance.getData();
        const normalizedData = this.formulaManager.normalizeSheetData(dataToUse, worksheetInstance.tableInstance);
        this.formulaManager.formulaEngine.updateSheetData(sheetDefine.sheetKey, normalizedData);
        if (sheetDefine.formulas && Object.keys(sheetDefine.formulas).length > 0) {
          this.loadFormulas(sheetDefine.sheetKey, sheetDefine.formulas);
        }
      });
      // 对于原来有公式但更新后移除的 sheet，显式清除公式状态
      updated.forEach(({ prev, next: next_sheetDefine }) => {
        const hadFormulas = prev.formulas && Object.keys(prev.formulas).length > 0;
        const hasFormulas = next_sheetDefine.formulas && Object.keys(next_sheetDefine.formulas).length > 0;
        if (!hadFormulas || hasFormulas) {
          return;
        }
        const worksheetInstance = this.workSheetInstances.get(next_sheetDefine.sheetKey);
        if (!worksheetInstance) {
          return;
        }
        const data = next_sheetDefine.data || worksheetInstance.getData();
        Object.keys(prev.formulas).forEach(cellRef => {
          try {
            const { row, col } = this.parseCellKey(cellRef);
            const cellValue = data?.[row]?.[col];
            if (typeof cellValue !== 'undefined') {
              this.formulaManager.setCellContent({ sheet: next_sheetDefine.sheetKey, row, col }, cellValue);
            }
          } catch (error) {
            console.warn(`Failed to clear formula for ${next_sheetDefine.sheetKey}!${cellRef}:`, error);
          }
        });
      });
    } catch (error) {
      console.error('Failed to rebuild formulas after sheets update:', error);
    }
    this.sheetManager.sortSheets(this.options.sheets);
    // 更新 sheet tab 和菜单
    this.updateSheetTabs();
    this.updateSheetMenu();

    // 保持当前激活 sheet：优先使用新配置中的 active 标记，否则回退到第一个 sheet
    const activeDefine = nextSheets.find(s => s.active) || nextSheets[0];
    if (activeDefine) {
      this.activateSheet(activeDefine.sheetKey);
    }
  }
  /** 导出当前sheet到文件 */
  exportSheetToFile(fileType: 'csv' | 'xlsx', allSheets: boolean = true): void {
    try {
      // 触发导出开始事件
      this.spreadsheetEventManager.emitExportStart(fileType, allSheets);

      const sheet = this.getActiveSheet();
      if (!sheet) {
        throw new Error('No active sheet available for export');
      }

      let sheetCount = 0;
      if (fileType === 'csv') {
        if ((sheet.tableInstance as any)?.exportToCsv) {
          (sheet.tableInstance as any).exportToCsv();
          sheetCount = 1;
        } else {
          throw new Error('TableExportPlugin not configured for CSV export');
        }
      } else {
        if (allSheets) {
          this.exportAllSheetsToExcel();
          sheetCount = this.sheetManager.getSheetCount();
        } else {
          if ((sheet.tableInstance as any)?.exportToExcel) {
            (sheet.tableInstance as any).exportToExcel();
            sheetCount = 1;
          } else {
            throw new Error('TableExportPlugin not configured for Excel export');
          }
        }
      }

      // 触发导出完成事件
      this.spreadsheetEventManager.emitExportCompleted(fileType, allSheets, sheetCount);
    } catch (error) {
      // 触发导出失败事件
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.spreadsheetEventManager.emitExportError(fileType, allSheets, errorMessage);
      console.warn('Export failed:', errorMessage);
    }
  }
  exportAllSheetsToExcel(): void {
    this.initAllSheetInstances();
    const allDefines = this.sheetManager.getAllSheets();
    const tables = allDefines.map(def => {
      const inst = this.workSheetInstances.get(def.sheetKey);
      if (!inst) {
        throw new Error(`Worksheet instance for key "${def.sheetKey}" not found during export`);
      }
      return { table: inst.tableInstance as any, name: def.sheetTitle || def.sheetKey };
    });
    (this as any)._exportMutipleTablesToExcel?.(tables); //这个方法是在vtable-plugins中添加的，table-export插件在VTableSheet实例上添加了导出所有sheet到Excel的方法
  }
  initAllSheetInstances(): void {
    const allDefines = this.sheetManager.getAllSheets();
    allDefines.forEach(def => {
      if (!this.workSheetInstances.has(def.sheetKey)) {
        const instance = this.createWorkSheetInstance(def);
        this.workSheetInstances.set(def.sheetKey, instance);
      }
    });
  }
  /**
   * 导入文件（支持 Excel 多 sheet 和 CSV）
   * @param options 导入选项，包括 clearExisting（是否清除现有 sheets，默认 true 表示替换模式）
   * @returns Promise<MultiSheetImportResult | void>
   */
  async importFileToSheet(
    options: { clearExisting?: boolean } = { clearExisting: true }
  ): Promise<MultiSheetImportResult | void> {
    try {
      // 触发导入开始事件
      this.spreadsheetEventManager.emitImportStart('xlsx');

      // 使用绑定到 VTableSheet 实例的导入方法（插件内部会处理文件选择）
      let result: MultiSheetImportResult | void;
      if ((this as any)?._importFile) {
        result = await (this as any)._importFile({
          clearExisting: options?.clearExisting !== false
        });
      } else {
        // 回退到 tableInstance 的 importFile 方法
        const sheet = this.getActiveSheet();
        if (!sheet) {
          throw new Error('No active sheet available for import');
        }
        if ((sheet.tableInstance as any)?.importFile) {
          result = await (sheet.tableInstance as any).importFile({
            clearExisting: options?.clearExisting !== false
          });
        } else {
          throw new Error('ExcelImportPlugin not configured');
        }
      }

      // 触发导入完成事件
      const sheetCount = result && 'sheets' in result ? result.sheets?.length || 0 : 0;
      this.spreadsheetEventManager.emitImportCompleted('xlsx', sheetCount);

      return result;
    } catch (error) {
      // 触发导入失败事件
      const errorMessage = error instanceof Error ? error.message : String(error);
      this.spreadsheetEventManager.emitImportError('xlsx', errorMessage);
      console.warn('Import failed:', errorMessage);
      throw error;
    }
  }
  /**
   * 获取容器元素
   */
  getContainer(): HTMLElement {
    return this.container;
  }

  /**
   * 获取根元素
   */
  getRootElement(): HTMLElement {
    return this.rootElement;
  }

  /**
   * 获取选项
   */
  getOptions(): IVTableSheetOptions {
    return this.options;
  }

  /**
   * 获取公式栏元素
   */
  getFormulaBarElement(): HTMLElement | null {
    return this.formulaBarElement;
  }

  /**
   * 获取sheet标签栏元素
   */
  getSheetTabElement(): HTMLElement | null {
    return this.sheetTabElement;
  }

  /**
   * 获取内容区域元素
   */
  getContentElement(): HTMLElement {
    return this.contentElement;
  }

  /**
   * 销毁实例
   */
  release(): void {
    // 触发电子表格销毁事件
    this.spreadsheetEventManager.emitDestroyed();

    // 清除所有 Table 事件监听器
    this.tableEventRelay.clearAllListeners();

    // 释放事件管理器
    this.eventManager.release();
    this.formulaManager.release();
    this.formulaUIManager.release();
    this.spreadsheetEventManager.clearAllListeners();

    // 释放菜单管理器
    if (this.menuManager) {
      this.menuManager.release();
    }

    // 移除点击外部监听器
    this.sheetTabEventHandler.removeClickOutsideListener();

    // 销毁所有sheet实例
    this.workSheetInstances.forEach(instance => {
      instance.release();
    });

    // 清空容器
    if (this.rootElement && this.rootElement.parentNode) {
      this.rootElement.parentNode.removeChild(this.rootElement);
    }

    if (this.formulaAutocomplete) {
      this.formulaAutocomplete.release();
    }
    if (this.formulaManager.cellHighlightManager) {
      this.formulaManager.cellHighlightManager.release();
    }
  }

  /**
   * 导出指定sheet的数据
   * @param sheetKey sheet的key
   * @returns 数据
   */
  exportData(sheetKey: string): SheetData {
    const sheet = this.workSheetInstances.get(sheetKey);
    if (!sheet) {
      return [];
    }
    return sheet.getData();
  }

  /**
   * 导出所有sheet的数据
   * @returns 数据
   */
  exportAllData(): SheetData[] {
    const sheets = Array.from(this.workSheetInstances.values());
    return sheets.map(sheet => sheet.getData());
  }

  /**
   * resize
   */
  resize(): void {
    // const containerWidth = this.getContainer().clientWidth;
    // const containerHeight = this.getContainer().clientHeight;
    // this.rootElement.style.width = `${this.getOptions().width || containerWidth}px`;
    // this.rootElement.style.height = `${this.getOptions().height || containerHeight}px`;
    this.getActiveSheet()?.resize();
  }
}
